/**
 * [INPUT]: 依赖 ./types 的 Landmark/Pose，./geometry 的 clamp01/mean，./geo_norm_params 的 confidence 权重
 * [OUTPUT]: 对外提供 poseScore / landmarkScore / confidence；对齐 docs §4.5
 * [POS]: apps/web/src/scorer 的置信度层，反映姿态/关键点/照片质量；UI 在 <0.55 提示仅供参考
 * [PROTOCOL]: 变更时更新此头部，然后检查 ../CLAUDE.md
 */
import type { Landmark, Pose } from './types'
import { clamp01, mean } from './geometry'
import { PARAMS } from './geo-features'

/** 姿态得分：偏离正脸越远分越低。yaw/pitch 权重高于 roll。 */
export function poseScore(pose: Pose): number {
  const yawPenalty = Math.min(Math.abs(pose.yaw) / 30, 1)
  const pitchPenalty = Math.min(Math.abs(pose.pitch) / 30, 1)
  const rollPenalty = Math.min(Math.abs(pose.roll) / 20, 1)
  return clamp01(1 - (0.4 * yawPenalty + 0.4 * pitchPenalty + 0.2 * rollPenalty))
}

/** 关键点得分：可见性均值；无 visibility 字段时默认 1（MediaPipe 默认全可见）。 */
export function landmarkScore(landmarks: Landmark[]): number {
  if (landmarks.length === 0) return 0
  return clamp01(mean(landmarks.map((l) => l.visibility ?? 1)))
}

export function confidence(args: {
  pose: Pose
  landmarks: Landmark[]
  photoQuality: number
}): number {
  const w = PARAMS.confidence_weights
  return clamp01(
    w.pose * poseScore(args.pose) +
      w.landmark * landmarkScore(args.landmarks) +
      w.photo_quality * clamp01(args.photoQuality),
  )
}